import { RefObject, useEffect, useState } from "react";
import gsap from "gsap";
import {
  TEXT_1,
  TEXT_2,
  TEXT_3,
  HEX_1,
  HEX_2,
  HEX_3,
  ARROW_1,
  ARROW_2,
  ARROW_3,
  LIGHT_UP_DURATION,
  FADE_OUT_DURATION,
  HOLD_DURATION,
  ARROW_DURATION,
  OVERLAP_DELAY,
} from "./OntologyFlywheelAdvantageSection.config";
import { PRIMARY_COLOR } from "../../theme/consts";

export const useFlywheelAnimation = (
  svgRef: RefObject<SVGSVGElement | null>,
  sectionRef: RefObject<HTMLElement | null>
) => {
  const [isInView, setIsInView] = useState(false);

  // Set initial inactive state immediately on mount
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!svgRef.current) return;

      gsap.set(svgRef.current.querySelectorAll(TEXT_1), {
        fill: PRIMARY_COLOR,
        opacity: 0.3,
        filter: "blur(2px)",
      });
      gsap.set(svgRef.current.querySelectorAll(TEXT_2 + "," + TEXT_3), {
        fill: PRIMARY_COLOR,
        opacity: 0.1,
        filter: "blur(3px)",
      });
      gsap.set(svgRef.current.querySelectorAll(HEX_1), {
        stroke: PRIMARY_COLOR,
        opacity: 0.3,
        filter: "blur(2px)",
      });
      gsap.set(svgRef.current.querySelectorAll(HEX_2 + "," + HEX_3), {
        stroke: PRIMARY_COLOR,
        opacity: 0.1,
        filter: "blur(3px)",
      });
      gsap.set(
        svgRef.current.querySelectorAll(
          ARROW_1 + "," + ARROW_2 + "," + ARROW_3
        ),
        {
          fill: PRIMARY_COLOR,
          opacity: 0.1,
        }
      );
    }, 50);

    return () => clearTimeout(timeout);
  }, [svgRef]);

  // Intersection Observer to detect when section is visible
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.3,
      }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, [sectionRef]);

  // GSAP animation - only starts when section is in view
  useEffect(() => {
    if (!isInView) return;

    const timeout = setTimeout(() => {
      if (!svgRef.current) return;

      const ctx = gsap.context(() => {
        gsap.set([TEXT_1], {
          fill: PRIMARY_COLOR,
          opacity: 0.3,
          filter: "blur(2px)",
        });
        gsap.set([TEXT_2, TEXT_3], {
          fill: PRIMARY_COLOR,
          opacity: 0.1,
          filter: "blur(3px)",
        });
        gsap.set([HEX_1], {
          stroke: PRIMARY_COLOR,
          opacity: 0.3,
          filter: "blur(2px)",
        });
        gsap.set([HEX_2, HEX_3], {
          stroke: PRIMARY_COLOR,
          opacity: 0.1,
          filter: "blur(3px)",
        });
        gsap.set([ARROW_1, ARROW_2, ARROW_3], {
          fill: PRIMARY_COLOR,
          opacity: 0.1,
        });

        const tl = gsap.timeline({ repeat: -1 });

        // STEP 1: Wheel 1 active (lights up fast)
        tl.to(TEXT_1, {
          fill: PRIMARY_COLOR,
          opacity: 1,
          filter: "blur(0px)",
          duration: LIGHT_UP_DURATION,
        })
          .to(
            HEX_1,
            {
              stroke: PRIMARY_COLOR,
              opacity: 1,
              filter: "blur(0px)",
              duration: LIGHT_UP_DURATION,
            },
            "<"
          )
          .to(
            TEXT_2,
            {
              fill: PRIMARY_COLOR,
              opacity: 0.3,
              filter: "blur(2px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to(
            HEX_2,
            {
              stroke: PRIMARY_COLOR,
              opacity: 0.3,
              filter: "blur(2px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to(
            TEXT_3,
            {
              fill: PRIMARY_COLOR,
              opacity: 0.1,
              filter: "blur(3px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to(
            HEX_3,
            {
              stroke: PRIMARY_COLOR,
              opacity: 0.1,
              filter: "blur(3px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to({}, { duration: HOLD_DURATION })

          // STEP 2: Arrow 1 appears
          .to(ARROW_1, {
            fill: PRIMARY_COLOR,
            opacity: 1,
            duration: ARROW_DURATION,
          })
          // STEP 3: Wheel 2 lights up fast (overlaps with arrow fadeout)
          .to(
            TEXT_2,
            {
              fill: PRIMARY_COLOR,
              opacity: 1,
              filter: "blur(0px)",
              duration: LIGHT_UP_DURATION,
            },
            `<${OVERLAP_DELAY}`
          )
          .to(
            HEX_2,
            {
              stroke: PRIMARY_COLOR,
              opacity: 1,
              filter: "blur(0px)",
              duration: LIGHT_UP_DURATION,
            },
            "<"
          )
          .to(
            ARROW_1,
            {
              fill: PRIMARY_COLOR,
              opacity: 0.1,
              duration: ARROW_DURATION,
            },
            "<"
          )
          .to(
            TEXT_1,
            {
              fill: PRIMARY_COLOR,
              opacity: 0.1,
              filter: "blur(3px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to(
            HEX_1,
            {
              stroke: PRIMARY_COLOR,
              opacity: 0.1,
              filter: "blur(3px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to(
            TEXT_3,
            {
              fill: PRIMARY_COLOR,
              opacity: 0.3,
              filter: "blur(2px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to(
            HEX_3,
            {
              stroke: PRIMARY_COLOR,
              opacity: 0.3,
              filter: "blur(2px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to({}, { duration: HOLD_DURATION })

          // STEP 4: Arrow 2 appears
          .to(ARROW_2, {
            fill: PRIMARY_COLOR,
            opacity: 1,
            duration: ARROW_DURATION,
          })
          // STEP 5: Wheel 3 lights up fast (overlaps with arrow fadeout)
          .to(
            TEXT_3,
            {
              fill: PRIMARY_COLOR,
              opacity: 1,
              filter: "blur(0px)",
              duration: LIGHT_UP_DURATION,
            },
            `<${OVERLAP_DELAY}`
          )
          .to(
            HEX_3,
            {
              stroke: PRIMARY_COLOR,
              opacity: 1,
              filter: "blur(0px)",
              duration: LIGHT_UP_DURATION,
            },
            "<"
          )
          .to(
            ARROW_2,
            {
              fill: PRIMARY_COLOR,
              opacity: 0.1,
              duration: ARROW_DURATION,
            },
            "<"
          )
          .to(
            TEXT_2,
            {
              fill: PRIMARY_COLOR,
              opacity: 0.1,
              filter: "blur(3px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to(
            HEX_2,
            {
              stroke: PRIMARY_COLOR,
              opacity: 0.1,
              filter: "blur(3px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to(
            TEXT_1,
            {
              fill: PRIMARY_COLOR,
              opacity: 0.3,
              filter: "blur(2px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to(
            HEX_1,
            {
              stroke: PRIMARY_COLOR,
              opacity: 0.3,
              filter: "blur(2px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to({}, { duration: HOLD_DURATION })

          // STEP 6: Arrow 3 appears
          .to(ARROW_3, {
            fill: PRIMARY_COLOR,
            opacity: 1,
            duration: ARROW_DURATION,
          })
          // STEP 7: Wheel 3 fades out (loop restarts, Wheel 2 change happens in Step 1)
          .to(
            ARROW_3,
            {
              fill: PRIMARY_COLOR,
              opacity: 0.1,
              duration: ARROW_DURATION,
            },
            `<${OVERLAP_DELAY}`
          )
          .to(
            TEXT_3,
            {
              fill: PRIMARY_COLOR,
              opacity: 0.1,
              filter: "blur(3px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          )
          .to(
            HEX_3,
            {
              stroke: PRIMARY_COLOR,
              opacity: 0.1,
              filter: "blur(3px)",
              duration: FADE_OUT_DURATION,
            },
            "<"
          );
      }, svgRef);

      return () => ctx.revert();
    }, 100);

    return () => clearTimeout(timeout);
  }, [isInView, svgRef]);
};
